import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Link2 } from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { Link as LinkType } from "../../../types";
import LinkForm, { LinkFormPayload } from "./LinkForm";
import Spinner from "../../../components/ui/Spinner";

// Full-page create/edit for a short link (replaces the old modal). Routes:
//   /:accountId/links/new        — create
//   /:accountId/links/:id/edit   — edit
const LinkBuilder = () => {
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const navigate = useNavigate();
  const services = useAppServices();
  const { buildPath } = useMarketplace();

  const [initial, setInitial] = useState<LinkType | null>(null);
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    (async () => {
      const { response } = await services.url_tracker.filter({
        query: `_id=${id}`,
      });
      if (cancelled) return;
      if (response) setInitial(response as LinkType);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const goBack = () => navigate(buildPath("links"));

  const handleSubmit = async (payload: LinkFormPayload) => {
    setError(null);
    setSubmitting(true);
    let res;
    if (editing) {
      res = await services.url_tracker.update({
        payload: { ...payload, _id: id },
      });
    } else {
      const { _id, ...createPayload } = payload;
      res = await services.url_tracker.create({ payload: createPayload });
    }
    setSubmitting(false);
    const err = (res as any).error;
    if (err) {
      setError(err.error || err.message || "Failed to save link");
      return;
    }
    goBack();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 mb-3"
        >
          <ArrowLeft size={12} /> Back to links
        </button>
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-brand-gradient flex items-center justify-center shadow-brand-glow shrink-0">
            <Link2 size={20} className="text-white" />
          </span>
          <div>
            <h1 className="font-display text-h1 text-ink-900">
              {editing ? "Edit link" : "Create a short link"}
            </h1>
            <p className="text-sm text-ink-500 mt-0.5">
              {editing
                ? "Update the destination, tracking, and rules."
                : "Paste a URL or pick a CRM page, then customize tracking."}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card p-5 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={28} />
          </div>
        ) : editing && !initial ? (
          <div className="text-center py-12">
            <p className="text-sm text-ink-500">Link not found.</p>
            <button
              onClick={goBack}
              className="mt-3 text-sm text-brand-600 hover:underline"
            >
              Back to links
            </button>
          </div>
        ) : (
          <LinkForm
            initial={initial}
            submitLabel={editing ? "Save changes" : "Create link"}
            submitting={submitting}
            error={error}
            onSubmit={handleSubmit}
            onCancel={goBack}
          />
        )}
      </div>
    </div>
  );
};

export default LinkBuilder;
